import { useEffect, useState, useContext } from "react"
import { DateTime } from "luxon"

//Notifications modules
import {
  noteApiListSystemNotificationsPOST,
  noteApiMarkSystemAsReadPut,
  noteApiMarkAllSystemAsReadPut,
  noteApiMarkSystemAsUnreadPut,
  noteApiListEventTypesGet,
} from "../notificationsApi"
import NotificationsFilters from "./NotificationsFilters"
import NotificationCreateSystemForm from "./NotificationCreateSystemForm"

//styles
import noteStyle from "../../../styles/notifications/NotificationsForm.module.css"

// Context
import { UserContext } from "../../../context/userContext"

//Helpers
import { LogDebug, LogError } from "../../../helpers/logger"
import { displayLocalDateTimeFromIso } from "../../../helpers/displayDates"

//prime react
import { Accordion, AccordionTab } from "primereact/accordion"
import { ProgressSpinner } from "primereact/progressspinner"
import { Paginator } from "primereact/paginator"
import { confirmDialog } from "primereact/confirmdialog"
import { Tooltip } from "primereact/tooltip"
import { Dialog } from "primereact/dialog"
import NotificationCard from "./NotificationCard"
import { TrendingDown } from "@material-ui/icons"

export default function NotificationsSystemTab(props) {
  const userCtx = useContext(UserContext)
  const isAllowedToCreate = userCtx.session.user.staff_role <= 3 // org admins are the lowest roles to be able to create

  const [pageSize, setPageSize] = useState(5)
  const [loading, setLoading] = useState(false)
  const [notifications, setNotifications] = useState(null)
  const [eventTypes, setEventTypes] = useState(null)
  const [refresh, setRefresh] = useState(new Date().getTime() - 10000)
  const [showCreateForm, setShowCreateForm] = useState(false)

  // Filters //
  const defaultFilters = {
    search: "",
    eventType: "All",
    createDateStart: userCtx.session?.user?.staff_create_date
      ? DateTime.fromISO(userCtx.session.user.staff_create_date).toJSDate()
      : undefined,
    createDateEnd: "",
    readDateStart: "",
    readDateEnd: "",
    sort: "newest",
    includeRead: false,
    includeUnread: true,
  }

  const [filters, setFilters] = useState(defaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters)

  function combineReadAndUnreadFilters() {
    const { includeRead, includeUnread } = appliedFilters

    if (includeRead && includeUnread) return null
    else if (includeRead && !includeUnread) return true
    else return false
  }

  async function getNotifications(page, currentPageSize) {
    setLoading(true)
    try {
      const res = await noteApiListSystemNotificationsPOST(
        userCtx.tokenNotification,
        userCtx,
        combineReadAndUnreadFilters(),
        appliedFilters.eventType,
        appliedFilters.createDateStart,
        appliedFilters.createDateEnd,
        appliedFilters.readDateStart,
        appliedFilters.readDateEnd,
        appliedFilters.search,
        appliedFilters.sort,
        page || 1,
        currentPageSize || pageSize,
      )

      if (res) {
        setNotifications(res)
      }
      LogDebug("System Notifications", res)
    } catch (error) {
      console.error("Unable to list system notifications", error)
      props.onShowError(
        "Unable to list system notifications. See console for details.",
      )
    }
    setLoading(false)
  }

  async function getEventTypes() {
    setLoading(true)
    try {
      const res = await noteApiListEventTypesGet(userCtx.tokenNotification)

      if (res) {
        setEventTypes(res)
      }
      LogDebug("EventTypes", res)
    } catch (error) {
      console.error("Unable to list event types", error)
      props.onShowError("Unable to list event types. See console for details.")
    }
    setLoading(false)
  }

  useEffect(() => {
    if (notifications) {
      getNotifications()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh])

  useEffect(() => {
    if (notifications !== null) {
      getNotifications()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters])

  useEffect(() => {
    if (!notifications) {
      getNotifications()
    } else if (!eventTypes) {
      getEventTypes()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, eventTypes])

  async function markAsRead(notificationId) {
    setLoading(true)

    try {
      await noteApiMarkSystemAsReadPut(
        userCtx.tokenNotification,
        notificationId,
        userCtx.session.user.staff_id,
      )
      props.onRefreshBellCounter(new Date().getTime())
      getNotifications()
      LogDebug(`Notification marked as read (ID: ${notificationId})`)
    } catch (error) {
      LogError(
        `Unable to mark notification as read (id: ${notificationId})`,
        error,
      )
      props.onShowError(
        `Unable to mark notification as read (id: ${notificationId}). See console for details.`,
      )
    }
    setLoading(false)
  }

  async function markAllAsRead() {
    if (!!notifications) {
      setLoading(true)

      await noteApiMarkAllSystemAsReadPut(userCtx.tokenNotification, userCtx)

      props.onRefreshBellCounter(new Date().getTime())
      setNotifications(null)
      getNotifications()

      setLoading(false)
    }
  }

  async function markAsUnread(notificationId) {
    setLoading(true)

    try {
      await noteApiMarkSystemAsUnreadPut(
        userCtx.tokenNotification,
        notificationId,
        userCtx.session.user.staff_id,
      )
      props.onRefreshBellCounter(new Date().getTime())
      getNotifications()
      LogDebug(`Notification marked as unread (ID: ${notificationId})`)
    } catch (error) {
      LogError(
        `Unable to mark notification as unread (id: ${notificationId})`,
        error,
      )
      props.onShowError(
        `Unable to mark notification as unread (id: ${notificationId}). See console for details.`,
      )
    }
    setLoading(false)
  }

  const filterHeader = () => {
    return (
      <div className={noteStyle.noteFilterHeaderGridContainer}>
        <div className={noteStyle.noteFilterHeader}>
          <label>Filters</label>
        </div>
        <div className={noteStyle.noteFilterButton}>
          <button
            type="button"
            className="btn"
            style={{
              float: "right",
              width: 40,
              height: 33,
              display: "flex",
              padding: "4px 9px",
            }}
            onClick={(e) => {
              e.stopPropagation()
              setRefresh(new Date().getTime())
            }}
          >
            <span
              className={`material-icons  p-overlay-badge ${noteStyle.NoteMarkRead}`}
            >
              {"autorenew"}
            </span>
          </button>
          <button
            type="button"
            className="btn t-tip"
            data-pr-tooltip="Mark All Notifications As Read"
            data-pr-position="bottom"
            style={{
              float: "right",
              width: 40,
              height: 33,
              display: "flex",
              padding: "4px 9px",
              marginRight: "10px",
            }}
            onClick={(e) => {
              e.stopPropagation()
              confirmDialog({
                message:
                  "Do you want to mark all unread notifications as read?",
                header: "Mark all as read",
                accept: () => markAllAsRead(),
              })
            }}
          >
            <span
              className={`material-icons  p-overlay-badge ${noteStyle.NoteMarkRead}`}
            >
              {"done_all"}
            </span>
          </button>
          {isAllowedToCreate ? (
            <button
              type="button"
              className="btn t-tip"
              data-pr-tooltip="Add New System Notification"
              data-pr-position="bottom"
              style={{
                float: "right",
                width: 40,
                height: 33,
                display: "flex",
                padding: "4px 9px",
                marginRight: "10px",
              }}
              onClick={(e) => {
                e.stopPropagation()
                setShowCreateForm(true)
              }}
            >
              <span
                className={`material-icons  p-overlay-badge ${noteStyle.NoteMarkRead}`}
              >
                {"add"}
              </span>
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  const onPageChange = (event) => {
    if (pageSize !== event.rows) {
      setPageSize(event.rows)
    }

    getNotifications(event.page + 1, event.rows)
  }

  return (
    <div>
      <Tooltip target=".t-tip" />
      <Dialog
        header="New system notification"
        visible={showCreateForm && isAllowedToCreate}
        onHide={() => {
          setShowCreateForm(false)
        }}
        style={{ width: "600px", height: "765px" }}
      >
        <NotificationCreateSystemForm
          onShowError={props.onShowError}
          onShowNotification={props.onShowNotification}
          onSetShowForm={setShowCreateForm}
          onRefreshNotifications={() => setRefresh(new Date().getTime())}
        />
      </Dialog>
      <div className={noteStyle.addLargeSpace}>
        <Accordion className={noteStyle.noteAccordion}>
          <AccordionTab header={filterHeader()}>
            <NotificationsFilters
              defaultFilters={defaultFilters}
              filters={filters}
              onSetFilters={setFilters}
              onApplyFilters={setAppliedFilters}
              eventTypes={eventTypes}
              isSystemTab={true}
            />
          </AccordionTab>
        </Accordion>
      </div>
      {loading ? (
        <div style={{ textAlign: "center" }}>
          <ProgressSpinner />
        </div>
      ) : (
        <div>
          {notifications && notifications.items.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "row" }}>
              <div style={{ width: "100%" }}>
                <Accordion
                  className={noteStyle.noteAccordion}
                  activeIndex={-1}
                  onTabChange={(e) => e.originalEvent.stopPropagation()}
                  expandIcon=""
                >
                  {notifications
                    ? notifications.items.map((x) => {
                        return NotificationCard({
                          notification: x,
                          getOrganizationName: props.getOrganizationName,
                          onMarkAsRead: markAsRead,
                          onMarkAsUnread: markAsUnread,
                          onActionButtonClick: () => {
                            LogError(
                              "System Notifications do not support action buttons",
                            )
                          },
                          onExpandDescription: () => {
                            setNotifications((prev) => ({
                              ...prev,
                              items: prev.items.map((n) =>
                                n.id === x.id
                                  ? { ...n, descriptionExpanded: true }
                                  : n,
                              ),
                            }))
                          },
                          onCollapseDescription: () => {
                            setNotifications((prev) => ({
                              ...prev,
                              items: prev.items.map((n) =>
                                n.id === x.id
                                  ? { ...n, descriptionExpanded: false }
                                  : n,
                              ),
                            }))
                          },
                        })
                      })
                    : null}
                </Accordion>

                <Paginator
                  first={notifications.pageSize * (notifications.pageNum - 1)}
                  rows={notifications.pageSize}
                  totalRecords={notifications.numTotal}
                  onPageChange={onPageChange}
                  rowsPerPageOptions={[5, 10, 20, 30]}
                />
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              No system notifications found
            </div>
          )}
        </div>
      )}
    </div>
  )
}
